import { Check, MoveRight, Printer, Square, X } from 'lucide-react';
import { TtqAngleRelationIcon, getTtqAngleRelation } from './TtqAngleRelationIcon';
import {
  getTtqBehaviorsForSelection,
  getTtqDeriveCanvasBehaviors,
  getTtqBirdsEyeBehaviors,
  describeTtqSelectionMode,
  isTtqBehaviorSelectable,
  isTtqBirdsEyeBehaviorReady,
  getTtqBehaviorLabel,
  type TtqBehaviorId,
  type TtqBehaviorListItem,
} from './ttqBehaviorCatalog';

type TtqBottomMenuProps = {
  visible: boolean;
  selectedParticleIds: string[];
  disabledBehaviorIds?: TtqBehaviorId[];
  /** 直接不展示（例如：衍生解法粒不再提供一题多解） */
  hiddenBehaviorIds?: TtqBehaviorId[];
  /** derive = 一对多画布；birds-eye = 仅多对一 */
  menuContext?: 'derive' | 'birds-eye' | 'auto';
  onClear: () => void;
  onSelectBehavior: (id: TtqBehaviorId) => void;
  onPrint?: () => void;
  triggering?: TtqBehaviorId | null;
};

function kindBadge(kind: TtqBehaviorListItem['kind']): string {
  switch (kind) {
    case 'one_to_many': return '一对多';
    case 'many_to_one': return '多对一';
    case 'many_to_many': return '多对多';
    default: return '';
  }
}

export function TtqBottomMenu({
  visible,
  selectedParticleIds,
  disabledBehaviorIds = [],
  hiddenBehaviorIds = [],
  menuContext = 'auto',
  onClear,
  onSelectBehavior,
  onPrint,
  triggering,
}: TtqBottomMenuProps) {
  const count = selectedParticleIds.length;
  if (!visible || count === 0) return null;

  const mode = describeTtqSelectionMode(count);
  const hiddenSet = new Set(hiddenBehaviorIds);
  const items = (menuContext === 'birds-eye'
    ? getTtqBirdsEyeBehaviors(count)
    : menuContext === 'derive'
      ? getTtqDeriveCanvasBehaviors(count)
      : getTtqBehaviorsForSelection(count)
  ).filter((item) => !hiddenSet.has(item.id));
  if (items.length === 0 && !onPrint) return null;
  const disabledSet = new Set(disabledBehaviorIds);
  const birdsEyeNeedMore = menuContext === 'birds-eye' && count < 2;

  return (
    <div className="fixed bottom-4 left-1/2 z-40 w-fit max-w-[calc(100vw-32px)] -translate-x-1/2 rounded-xl border border-border bg-surface shadow-lg">
      <div className="flex items-center justify-between border-b border-border px-4 py-2">
        <div className="flex items-center gap-2 text-sm">
          <span className="font-semibold text-ink">
            {menuContext === 'birds-eye' ? '多对一 · 聚合' : '三一思考 · 品题行为'}
          </span>
          {birdsEyeNeedMore ? null : (
            <span className="text-xs text-muted">
              {mode === 'single' ? '已选 1 颗颗粒' : `已选 ${count} 颗颗粒`}
            </span>
          )}
        </div>
        <button type="button" onClick={onClear} className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs text-muted hover:bg-black/[0.05]">
          <X size={14} />
          清空
        </button>
        {onPrint ? (
          <button
            type="button"
            onClick={onPrint}
            className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-semibold text-primary hover:bg-primary/10"
          >
            <Printer size={14} />
            打印
          </button>
        ) : null}
      </div>

      {birdsEyeNeedMore ? (
        <div className="border-b border-amber-200/80 bg-amber-50 px-4 py-2 text-xs text-amber-900">
          至少选择 2 个六边形后，才可开始多对一聚合（当前已选 {count}）
        </div>
      ) : null}

      <div className="overflow-x-auto overscroll-x-contain px-3 py-3 [-webkit-overflow-scrolling:touch]">
        <div className="mx-auto flex w-max max-w-full flex-nowrap justify-start gap-2">
        {items.map((item) => {
          const baseSelectable = isTtqBehaviorSelectable(item) && !disabledSet.has(item.id);
          const ready = menuContext === 'birds-eye'
            ? isTtqBirdsEyeBehaviorReady(item, count)
            : true;
          const selectable = baseSelectable && ready;
          const isBusy = triggering === item.id;
          return (
            <button
              key={item.id}
              type="button"
              disabled={!selectable || Boolean(triggering)}
              onClick={() => selectable && onSelectBehavior(item.id)}
              className={
                'flex w-[220px] shrink-0 flex-col items-start rounded-lg border px-3 py-2 text-left transition-colors '
                + (selectable ? 'border-primary/30 bg-primary/5 hover:bg-primary/10' : 'cursor-not-allowed border-border bg-black/[0.02] text-muted')
              }
            >
              <div className="flex w-full items-start justify-between gap-2">
                {getTtqAngleRelation(item.id) ? (
                  <TtqAngleRelationIcon angleId={item.id} size="sm" muted={!selectable} />
                ) : (
                  <span className="text-sm font-medium text-ink">{getTtqBehaviorLabel(item.id)}</span>
                )}
                <span className="shrink-0 text-[10px] font-medium text-muted">{kindBadge(item.kind)}</span>
              </div>
              <p className="mt-1 line-clamp-2 text-xs text-muted">{item.description}</p>
              {isBusy ? <span className="mt-1 text-[10px] text-primary">执行中…</span> : null}
            </button>
          );
        })}
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 border-t border-border px-4 py-2 text-[10px] text-muted">
        <span className="font-medium text-ink/70">图例</span>
        <span className="inline-flex items-center gap-1"><Square size={12} />题</span>
        <span className="inline-flex items-center gap-1"><Check size={12} />解</span>
        <span className="inline-flex items-center gap-1"><MoveRight size={12} />变式</span>
      </div>
    </div>
  );
}
